import { LoadingButton } from '@atlaskit/button';
import { ApolloQueryResult } from '@apollo/client';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import {
  ErrorComponents,
  InfoPanel,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { markUncollectible } from '@atlassian/temp-proxies';
import { BillingDocWithLineItems, InvoiceWithLineItems, InvoiceRequest } from '@atlassian/cssxp-types-billing';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import React from 'react';

interface Props {
  billingDoc?: BillingDocWithLineItems;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

const InvoiceUncollectiblePane = ({ billingDoc, refetch }: Props): JSX.Element | null => {
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<InvoiceRequest, any>({
    submitFormAction: markUncollectible,
    onComplete: () => {
      if (refetch) {
        refetch();
      }
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const { closeSidebar } = useSidebar();
  const invoice = billingDoc as InvoiceWithLineItems;
  const transactionAccountId = invoice?.transactionAccountId ?? '';
  if (!invoice || invoice?.sourceSystem === SourceSystem.HAMS) {
    return null;
  }
  const requestInvoiceUpdateData = () => {
    const requestData = {
      invoiceId: invoice.id,
      transactionAccountId,
    };
    return requestData;
  };

  const handleFormSubmit = () => {
    const requestData = requestInvoiceUpdateData();
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-uncollectible-success')
      .withMessage('Successfully updated the status to uncollectible.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);

    closeSidebar();
  }

  return (
    <div
      className="invoice-uncollectible-container"
      style={{ maxWidth: '500px' }}
      data-testId="invoice-uncollectible-container"
    >
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={'Mark uncollectible'}></FormHeader>
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the invoice for the following reason"
                      message={formError}
                    />
                  }
                />
              ) : !transactionAccountId ? (
                <InfoPanel
                  type="warning"
                  title={'Cannot update status'}
                  message={'There is no transaction account id on this invoice'}
                />
              ) : (
                <InfoPanel
                  type="information"
                  title="Exercise this option only when all the subscriptions in the respective invoice are requested to be cancelled. "
                  message={''}
                />
              )}
            </FormSection>

            <FormFooter align="start">
              <LoadingButton appearance="primary" type="submit" isLoading={isProcessing} data-testId="update-invoice">
                {'Mark uncollectible'}
              </LoadingButton>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default React.memo(InvoiceUncollectiblePane);
